// Auto-updater integration. Tauri 2's updater plugin doesn't auto-poll
// on app start — you have to call check() explicitly from JS. This hook
// runs the check ~3 seconds after the app mounts (after login UI is
// painted, so the network noise doesn't compete with auth requests),
// and on success prompts the user to install.
//
// Web build is a no-op — isTauri() is the early bail.

import { useEffect, useState } from "react";
import { isTauri } from "@/lib/desktop";
import { toast } from "sonner";

export type UpdateState =
  | { kind: "idle" }
  | { kind: "checking" }
  | { kind: "no-update"; currentVersion: string }
  | { kind: "available"; currentVersion: string; nextVersion: string }
  | { kind: "downloading"; nextVersion: string }
  | { kind: "installed"; nextVersion: string }
  | { kind: "error"; message: string };

let HAS_RUN_AUTO_CHECK = false;

/**
 * Mount once at the app root. Runs an automatic update check after a
 * short delay; shows a toast + a "Update now" prompt if an update is
 * available; tracks state for callers that want to render their own
 * UI (e.g. the Settings page can show "Check for updates" button).
 */
export function useAutoUpdater(opts: { autoRun?: boolean } = {}) {
  const autoRun = opts.autoRun ?? true;
  const [state, setState] = useState<UpdateState>({ kind: "idle" });

  const check = async (silent: boolean = false): Promise<UpdateState> => {
    if (!isTauri()) {
      const result: UpdateState = { kind: "no-update", currentVersion: "web" };
      setState(result);
      return result;
    }
    setState({ kind: "checking" });
    try {
      const [{ check }, { getVersion }, { relaunch }] = await Promise.all([
        import("@tauri-apps/plugin-updater"),
        import("@tauri-apps/api/app"),
        import("@tauri-apps/plugin-process"),
      ]);
      const currentVersion = await getVersion();
      const update = await check();
      if (!update) {
        const result: UpdateState = { kind: "no-update", currentVersion };
        setState(result);
        if (!silent) {
          toast.success("You're up to date", {
            description: `Running v${currentVersion}.`,
          });
        }
        return result;
      }

      const available: UpdateState = {
        kind: "available",
        currentVersion,
        nextVersion: update.version,
      };
      setState(available);

      // Hand the user a clear choice. We don't auto-install — too rude
      // to interrupt mid-photography session — but we make it one click.
      toast.info(`Update available: v${update.version}`, {
        description: `Currently on v${currentVersion}. Click Install to apply.`,
        duration: 30_000,
        action: {
          label: "Install",
          onClick: async () => {
            try {
              setState({ kind: "downloading", nextVersion: update.version });
              await update.downloadAndInstall();
              setState({ kind: "installed", nextVersion: update.version });
              toast.success("Update installed — restarting…");
              setTimeout(() => void relaunch(), 1000);
            } catch (err) {
              const msg = err instanceof Error ? err.message : String(err);
              setState({ kind: "error", message: msg });
              toast.error("Update install failed", { description: msg });
            }
          },
        },
      });

      return available;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const result: UpdateState = { kind: "error", message: msg };
      setState(result);
      if (!silent) {
        toast.error("Update check failed", { description: msg });
      }
      return result;
    }
  };

  useEffect(() => {
    if (!autoRun) return;
    if (HAS_RUN_AUTO_CHECK) return; // De-dupe across hot reloads + remounts.
    HAS_RUN_AUTO_CHECK = true;
    if (!isTauri()) return;
    // Delay so the check doesn't compete with auth + initial data fetches
    // for socket bandwidth on slow networks.
    const t = window.setTimeout(() => void check(true), 3000);
    return () => window.clearTimeout(t);
  }, [autoRun]);

  return { state, check };
}
